// Set up google analytics 4 (GA4)

import dayjs from 'dayjs';
import TagManager from 'react-gtm-module';
import { getAttribution, initializeAttribution } from '../attribution';

var dataLayer = dataLayer;

export function setupGTM(countryCode) {
  try {
    console.log('Initializing GTM...');

    if (typeof countryCode === 'string' && countryCode.length > 0) {
      const tags = {
        SE: 'GTM-5T7N7W5',
        DK: 'GTM-N6KVGSRB',
        NO: 'GTM-5CLSND4V',
        FI: 'GTM-M99T5Q4C',
        DE: 'GTM-NNNCPVK3'
      };

      const gtmTagId = tags[countryCode];

      if (gtmTagId) {
        // Ensure that GTM is not already initialized
        if (!window.dataLayer) {
          const tagManagerArgs = { gtmId: gtmTagId };
          TagManager.initialize(tagManagerArgs);
        } else {
          console.log('GTM is already initialized');
        }
      } else {
        console.warn('No GTM Tag ID found for the provided country code:', countryCode);
      }
    } else {
      console.warn('Invalid country code:', countryCode);
    }
  } catch (error) {
    console.error('Failed to add GTM tag:', error);
  }
}

/**
 * Campaign attribution for this tab (lib/attribution.ts), flattened onto
 * every funnel event so sessions that don't book can be attributed too.
 * Only the click id's type is sent, never the id; referrer and landing
 * page are reduced to host and path.
 */
export function attributionFields() {
  // The page's first event can fire before _app has initialised
  // attribution (child effects run first), so initialise it here if needed.
  const a = getAttribution() || (typeof window !== 'undefined' ? initializeAttribution() : null);
  if (!a) return {};
  const fields = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'].forEach((key) => {
    if (a[key]) fields[key] = a[key];
  });
  const clickIdType = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'oppref'].find((key) => a[key]) ?? null;
  if (clickIdType) fields['click id type'] = clickIdType;
  try {
    if (a.referrer && a.referrer !== 'direct') fields['referrer host'] = new URL(a.referrer).hostname;
  } catch (e) {
    // Not a URL; leave it out.
  }
  try {
    if (a.landing_page) fields['landing path'] = new URL(a.landing_page).pathname;
  } catch (e) {
    // Not a URL; leave it out.
  }
  return fields;
}

export function sendToDatalayer(data) {
  // Disable for production for now
  // if (process.env.NODE_ENV === 'production') {
  //   return;
  // }
  try {
    data.bookingflow_name = 'booking-2.0';
    const attribution = attributionFields();
    Object.keys(attribution).forEach((key) => {
      if (data[key] === undefined) data[key] = attribution[key];
    });
    window.dataLayer = window.dataLayer || [];
    // Intentionally NOT logging `window.dataLayer` here: once GTM is
    // initialised it can contain circular references (back-pointers to
    // window/document and the GTM internals). With Sentry's default
    // console breadcrumb integration that serialization is what was
    // surfacing as "RangeError: Maximum call stack size exceeded" with
    // an opaque `at ? (undefined:38:249)` stack.
    window.dataLayer.push(data);
    console.log('GA4 data', data);
  } catch (error) {
    console.error('Failed to send to datalayer', error);
  }
}

// `booking_flow_started` fires on the count page's mount, and the count page
// re-mounts on every back-navigation. Remember the venue the current flow
// started at (per tab) so the event fires once per flow; a submit ends the
// flow, so the next booking in the same tab starts a new one.
const FLOW_STARTED_KEY = 'ga4FlowStartedVenue';

function flowAlreadyStarted(venueId) {
  try {
    return sessionStorage.getItem(FLOW_STARTED_KEY) === String(venueId);
  } catch (e) {
    return false;
  }
}

function markFlowStarted(venueId) {
  try {
    sessionStorage.setItem(FLOW_STARTED_KEY, String(venueId));
  } catch (e) {
    // Storage blocked: fall back to firing on every mount.
  }
}

function endFlow() {
  try {
    sessionStorage.removeItem(FLOW_STARTED_KEY);
  } catch (e) {
    // Nothing to clear.
  }
}

function formatSeatingTime(hour, minute) {
  if (hour === undefined || hour === null || minute === undefined || minute === null) return null;
  const h = hour.toString().padStart(2, '0');
  const m = minute.toString().padStart(2, '0');
  return `${h}:${m}`;
}

export function ga4FormStarted(isLargeBooking, prefilled) {
  sendToDatalayer({
    event: isLargeBooking ? 'bookingrequest_flow_form_start' : 'booking_flow_form_start',
    prefilled: prefilled,
  });
}

export function ga4SendMessage() {
  sendToDatalayer({
    event: 'send_message',
  });
}

/**
 * Fired client-side when a reservation submission is rejected by the
 * bot-protection layer (Cloudflare Turnstile / honeypot / time-trap).
 * Use this in GA4 to monitor the false-positive rate alongside the
 * existing `*_form_submit` funnel events.
 *
 * @param {('reservation'|'request'|'waitlist')} flow Which booking flow.
 * @param {string} [venueId]
 */
export function ga4BotProtectionBlocked(flow, venueId) {
  sendToDatalayer({
    event: 'bot_protection_blocked',
    flow,
    restaurant: venueId,
  });
}

export function ga4FormSubmit(
  bookingId,
  countryCode,
  venueId,
  seatings,
  kids,
  date,
  hour,
  minute,
  isApp,
) {
  endFlow();
  sendToDatalayer({
    event: 'booking_flow_form_submit',
    'booking id': bookingId,
    market: countryCode,
    restaurant: venueId,
    'amount of total guests': seatings,
    'amount of child guest': kids,
    'seating date': dayjs(date).format('YYYYMMDD'),
    'seating time': `${hour.length < 2 ? '0' + hour.toString() : hour}:${minute.toString() == '0' ? '00' : minute}`,
    'booking date': dayjs().format('YYYYMMDD'),
    'app booking': isApp,
    // login_status: ,
    // membership_status: ,
    // real_lifetime_value: ,
    // pebv: ,
  });
}

export function ga4RequestFormSubmit(
  bookingId,
  countryCode,
  venueId,
  seatings,
  kids,
  date,
  hour,
  minute,
  isApp,
) {
  endFlow();
  sendToDatalayer({
    event: 'bookingrequest_flow_form_submit',
    'booking id': bookingId,
    market: countryCode,
    restaurant: venueId,
    'amount of total guests': seatings,
    'amount of child guest': kids,
    'seating date': dayjs(date).format('YYYYMMDD'),
    'seating time': `${hour.length < 2 ? '0' + hour.toString() : hour}:${minute.toString() == '0' ? '00' : minute}`,
    'booking date': dayjs().format('YYYYMMDD'),
    'app booking': isApp,
    // login_status: ,
    // membership_status: ,
    // real_lifetime_value: ,
    // pebv: ,
  });
}

/**
 * @param {('date'|'choose-time')} [source] `date` is the date step's Continue;
 *   `choose-time` is the date strip on the all-times page, i.e. a guest
 *   switching day after seeing that day's times.
 */
export function ga4SelectedDate(year, month, day, source = 'date') {
  sendToDatalayer({
    event: 'booking_flow_selected_seating_date',
    'seating date': dayjs(year + '-' + month + '-' + day).format('YYYYMMDD'),
    'booking date': dayjs().format('YYYYMMDD'),
    source,
  });
}

export function ga4SelectedGuests(seatings, kids) {
  sendToDatalayer({
    event: 'booking_flow_selected_guests',
    'amount of total guest': seatings,
    'amount of child guest': kids,
  });
}

export function ga4SelectedTime(hour, minute, requestedTime, suggestedTime, suggestedTimeShown) {
  console.log('Req time', requestedTime);
  sendToDatalayer({
    event: 'booking_flow_selected_seating_time',
    'seating time': `${hour.length < 2 ? '0' + hour.toString() : hour}:${minute.toString() == '0' ? '00' : minute}`,
    'requested seating time': requestedTime,
    'suggested seating time': suggestedTime,
    'shown suggested seating time': suggestedTimeShown,
  });
}

export function ga4CancelReservation(bookingId) {
  sendToDatalayer({
    event: 'booking_cancellation',
    bookingId,
  });
}

export function ga4BookingFlowStarted(countryCode, venueId, isApp) {
  if (flowAlreadyStarted(venueId)) return;
  markFlowStarted(venueId);
  sendToDatalayer({
    event: 'booking_flow_started',
    market: countryCode,
    restaurant: venueId,
    'app booking': isApp,
    // login_status: '0701234566',
  });
}

export function ga4TimeConflict() {
  sendToDatalayer({
    event: 'booking_time_conflict',
  });
}

export function ga4PreorderPaymentStart(bookingId, settlement) {
  sendToDatalayer({
    event: 'preorder_payment_start',
    bookingId,
    preorder_settlement: settlement,
  });
}

export function ga4PreorderPaymentSuccess(bookingId, settlement, status) {
  sendToDatalayer({
    event: 'preorder_payment_success',
    bookingId,
    preorder_settlement: settlement,
    payment_status: status,
  });
}

/**
 * Fired once per date/time/party-size/zone when availability has loaded, so
 * "the guest's time was full" can be told apart from "the guest left".
 *
 * Outcomes by source:
 * - `suggestion` (after the time step): `available` (the requested slot is
 *   free), `alternatives` (not free, nearby slots offered), `none_nearby`
 *   (nothing free near it; the guest is sent to all times), `error`.
 * - `choose-time` (all times for the day): `available-slots`,
 *   `fully-booked`, `zone-fully-booked` (only the other zone has room),
 *   `zone-closed`, `closed`.
 */
export function ga4AvailabilityResult({
  source,
  outcome,
  venueId = null,
  date = null,
  hour = null,
  minute = null,
  seatings = null,
  zone = null,
  freeSlots = null,
  occupiedSlots = null,
  waitlistAvailable = null,
}) {
  sendToDatalayer({
    event: 'booking_flow_availability_result',
    source,
    outcome,
    restaurant: venueId,
    'seating date': date ? dayjs(date).format('YYYYMMDD') : null,
    'requested seating time': formatSeatingTime(hour, minute),
    'amount of total guests': seatings,
    zone,
    'free slots': freeSlots ?? null,
    'occupied slots': occupiedSlots ?? null,
    'waitlist available': waitlistAvailable,
  });
}

/** "View all times" on the time step, before any availability is shown. */
export function ga4ViewAllTimes(hour, minute) {
  sendToDatalayer({
    event: 'booking_flow_view_all_times',
    'requested seating time': formatSeatingTime(hour, minute),
  });
}

/** A slot at a sister restaurant (RelatedVenues) was picked. */
export function ga4RelatedVenueSelected(fromVenueId, toVenueId, hour, minute) {
  sendToDatalayer({
    event: 'booking_flow_related_venue_selected',
    restaurant: fromVenueId,
    'related restaurant': toVenueId,
    'seating time': formatSeatingTime(hour, minute),
  });
}

export function ga4WaitlistStart(venueId, seatings, waitlistQuartiles) {
  sendToDatalayer({
    event: 'booking_flow_waitlist_start',
    restaurant: venueId,
    'amount of total guests': seatings,
    'waitlist slots': waitlistQuartiles?.length ?? 0,
  });
}

export function ga4WaitlistSubmit(
  bookingId,
  countryCode,
  venueId,
  seatings,
  kids,
  date,
  hour,
  minute,
  waitlistQuartiles,
  isApp,
) {
  endFlow();
  sendToDatalayer({
    event: 'booking_flow_waitlist_submit',
    'booking id': bookingId,
    market: countryCode,
    restaurant: venueId,
    'amount of total guests': seatings,
    'amount of child guest': kids,
    'seating date': dayjs(date).format('YYYYMMDD'),
    'seating time': formatSeatingTime(hour, minute),
    'booking date': dayjs().format('YYYYMMDD'),
    'waitlist slots': waitlistQuartiles?.length ?? 0,
    'app booking': isApp,
  });
}
